import { useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tag } from "@/components/ui/tag";
import { Textarea } from "@/components/ui/textarea";

import { FirmPortalAPI } from "../../../clientPortal/api";
import {
  refreshClientPortal,
  useFirmPortalCases
} from "../../../clientPortal/queries";
import type {
  FirmPortalCase,
  FirmPortalCaseUpdate,
  PortalStageOption,
  StageKey
} from "../../../clientPortal/types";
import { SettingsFieldRow, SettingsSection } from "./SettingsSection";

const AUTO = "auto";

const CaseRow: FC<{
  clientId: string;
  row: FirmPortalCase;
  stages: PortalStageOption[];
}> = ({ clientId, row, stages }) => {
  const [note, setNote] = useState(row.note ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setNote(row.note ?? ""), [row.note]);

  const label = (key: StageKey) =>
    stages.find((s) => s.key === key)?.label ?? key;

  const save = async (patch: FirmPortalCaseUpdate, done: string) => {
    setSaving(true);
    try {
      await FirmPortalAPI.updateCase(clientId, row.id, patch);
      toast.success(done);
    } catch (e) {
      toast.error("Couldn't update what the client sees", {
        description: e instanceof Error ? e.message : undefined
      });
    } finally {
      setSaving(false);
      await refreshClientPortal();
    }
  };

  const noteDirty = note.trim() !== (row.note ?? "").trim();
  const switchId = `client-view-shared-${row.id}`;
  const stageId = `client-view-stage-${row.id}`;
  const noteId = `client-view-note-${row.id}`;
  return (
    <>
      <div
        className="flex min-w-0 items-center gap-2 px-4 py-2.5"
        data-testid="client-view-case"
      >
        <Tag>{row.title}</Tag>
        <span className="min-w-0 flex-1 truncate text-base">
          {row.closed
            ? "Closed. Closed cases are hidden from the client."
            : row.shared
            ? `The client sees: ${label(row.stage)}`
            : "Hidden from the client."}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <Label htmlFor={switchId} className="text-sm font-normal text-fg-3">
            Shown
          </Label>
          <Switch
            id={switchId}
            checked={row.shared && !row.closed}
            disabled={saving || row.closed}
            onCheckedChange={(shared) =>
              void save(
                { shared },
                shared
                  ? "The client can see this case"
                  : "Case hidden from the client"
              )
            }
          />
        </span>
      </div>
      {row.shared && !row.closed ? (
        <>
          <SettingsFieldRow label="Stage" htmlFor={stageId}>
            <Select
              value={row.stage_override ?? AUTO}
              disabled={saving}
              onValueChange={(value) =>
                void save(
                  { stage: value === AUTO ? null : (value as StageKey) },
                  "Stage updated"
                )
              }
            >
              <SelectTrigger id={stageId} className="w-full" aria-label="Stage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO}>
                  Automatic ({label(row.derived_stage)})
                </SelectItem>
                {stages.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SettingsFieldRow>
          <SettingsFieldRow
            label="What's Happening Now"
            htmlFor={noteId}
            hint="Optional. Replaces the standard text."
          >
            <Textarea
              id={noteId}
              rows={2}
              value={note}
              placeholder={row.explainer_custom ? undefined : row.explainer}
              maxLength={600}
              onChange={(e) => setNote(e.currentTarget.value)}
            />
            {noteDirty ? (
              <span className="flex gap-1.5">
                <Button
                  variant="secondary"
                  disabled={saving}
                  onClick={() =>
                    void save({ note: note.trim() || null }, "Saved")
                  }
                >
                  Save
                </Button>
                <Button variant="ghost" onClick={() => setNote(row.note ?? "")}>
                  Cancel
                </Button>
              </span>
            ) : null}
          </SettingsFieldRow>
        </>
      ) : null}
    </>
  );
};

/** Per case: whether the client sees it, which stage, and in whose words. */
export const ClientViewCard: FC<{ clientId: string }> = ({ clientId }) => {
  const cases = useFirmPortalCases(clientId);
  const description =
    "When a shown case moves forward a stage, the client gets an email (only while their portal is on).";
  if (cases.isLoading)
    return (
      <SettingsSection title="What the Client Sees" description={description}>
        <div className="px-4 py-3">
          <Skeleton className="h-16 w-full" />
        </div>
      </SettingsSection>
    );
  const rows = cases.data?.cases ?? [];
  if (!rows.length) return null;
  return (
    <SettingsSection title="What the Client Sees" description={description}>
      {rows.map((row) => (
        <CaseRow
          key={row.id}
          clientId={clientId}
          row={row}
          stages={cases.data?.stages ?? []}
        />
      ))}
    </SettingsSection>
  );
};
